// intake.js — Phase 3.2: Intake UI
// Three-tier intake feed: Tier 1 auto-filed, Tier 2 needs review, Tier 3 never shown.

import { apiFetch, formatDate } from './utils.js';
import { state } from './state.js';

export function initIntake() {
  // Expose globally so app-legacy.js nav handler can call it
  window.renderIntakeView = renderIntakeView;
}

export async function renderIntakeView() {
  const container = document.getElementById('view-intake');
  if (!container) return;

  container.innerHTML = `
    <div class="intake-wrapper">
      ${renderIntakeHeader()}
      <div class="intake-body">
        <div class="intake-feed" id="intake-feed">
          <div class="loading-state">Loading...</div>
        </div>
        <div class="intake-detail" id="intake-detail">
          <div class="detail-placeholder">Select an item to review</div>
        </div>
      </div>
    </div>
  `;

  await loadIntakeData();
  setupIntakeHandlers();
}

function renderIntakeHeader() {
  return `
    <div class="intake-header">
      <div class="intake-title">
        <h2>Intake</h2>
        <div class="intake-source-tabs">
          <button class="source-tab active" data-source="all">All Inboxes</button>
          <button class="source-tab" data-source="wmm">WMM Gmail</button>
          <button class="source-tab" data-source="personal">Personal Gmail</button>
          <button class="source-tab" data-source="imessage">iMessage</button>
        </div>
        <button class="btn-scan-intake" id="btn-scan-intake" onclick="scanIntakeNow()">⟳ Scan Now</button>
      </div>
      <div class="intake-stats-bar" id="intake-stats-bar"></div>
      <div class="read-status-badge">
        <span class="badge-dot amber"></span>
        Unread = needs your attention
      </div>
    </div>
  `;
}

async function loadIntakeData() {
  const source = state.intakeSource || 'all';
  const url = source === 'all' ? '/api/intake/feed' : `/api/intake/feed?source=${source}`;

  try {
    const [feedItems, stats] = await Promise.all([
      apiFetch(url),
      apiFetch('/api/intake/stats')
    ]);
    renderIntakeFeed(feedItems);
    renderIntakeStats(stats);
  } catch (err) {
    console.error('Error loading intake:', err);
    const feed = document.getElementById('intake-feed');
    if (feed) feed.innerHTML = '<div class="error-state">Failed to load intake feed</div>';
  }
}

function renderIntakeStats(stats) {
  const bar = document.getElementById('intake-stats-bar');
  if (!bar) return;
  bar.innerHTML = `
    <span class="stat-item needs-review">
      <strong>${stats.needs_review ?? 0}</strong> needs review · unread in Gmail
    </span>
    <span class="stat-sep">·</span>
    <span class="stat-item auto-filed">
      <strong>${stats.auto_filed ?? 0}</strong> auto-filed · marked read
    </span>
    <span class="stat-sep">·</span>
    <span class="stat-item filtered">
      <strong>${stats.filtered_out ?? 0}</strong> filtered — never shown
    </span>
  `;
}

function renderIntakeFeed(items) {
  const feed = document.getElementById('intake-feed');
  if (!feed) return;

  if (!items.length) {
    feed.innerHTML = '<div class="empty-state">No items to review</div>';
    return;
  }

  const needsReview = items.filter(i => i.tier === 2 && !i.user_action);
  const autoFiled   = items.filter(i => i.tier === 1);

  let html = '';

  if (needsReview.length) {
    html += `
      <div class="feed-section-label needs-review-label">
        <span class="unread-dot"></span>
        Needs Review · Unread in Gmail
      </div>
    `;
    html += needsReview.map(item => renderFeedItem(item, 'needs-review')).join('');
  }

  if (autoFiled.length) {
    html += `<div class="feed-section-label auto-filed-label">Auto-Filed · Marked Read in Gmail</div>`;
    html += autoFiled.map(item => renderFeedItem(item, 'auto-filed')).join('');
  }

  feed.innerHTML = html;
}

function renderFeedItem(item, sectionClass) {
  const isAutoFiled = sectionClass === 'auto-filed';
  const clientName  = item.client_name ? `<span class="client-tag">${item.client_name}</span>` : '';

  return `
    <div class="feed-item ${sectionClass} ${isAutoFiled ? 'muted' : ''}"
         data-id="${item.id}"
         onclick="selectIntakeItem(${item.id})">
      <div class="feed-item-header">
        <span class="sender-name">${item.sender_name || item.sender_email || 'Unknown'}</span>
        ${clientName}
        <span class="feed-time">${formatDate(item.received_at)}</span>
      </div>
      <div class="feed-item-subject">${item.subject || '(no subject)'}</div>
      ${item.source_account !== 'wmm' ? `<div class="feed-item-source">${item.source_account}</div>` : ''}
    </div>
  `;
}

function renderIntakeDetail(item) {
  const detail = document.getElementById('intake-detail');
  if (!detail) return;

  const isNeedsReview = item.tier === 2 && !item.user_action;

  detail.innerHTML = `
    <div class="detail-panel">
      <div class="detail-header">
        <div class="detail-sender">
          <strong>${item.sender_name || item.sender_email || 'Unknown'}</strong>
          ${item.sender_email ? `<span class="detail-email">${item.sender_email}</span>` : ''}
        </div>
        <div class="detail-meta">
          <span class="detail-source">${item.source_account || ''}</span>
          <span class="detail-time">${formatDate(item.received_at)}</span>
        </div>
      </div>
      <div class="detail-subject">${item.subject || '(no subject)'}</div>
      <div class="detail-body">${item.body_preview || ''}</div>
      <div class="detail-classification">
        <span class="tier-badge tier-${item.tier}">Tier ${item.tier}</span>
        <span class="confidence-badge">${Math.round((item.confidence || 0) * 100)}% confidence</span>
        <span class="label-badge">${item.classification_label || 'unknown'}</span>
      </div>
      ${isNeedsReview ? renderRoutingPanel(item) : renderAutoFiledStatus(item)}
    </div>
  `;
}

function renderRoutingPanel(item) {
  const currentYear = new Date().getFullYear();
  const clients = (state.clients || []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  const current  = clients.filter(c => parseInt(c.latest_season_year) === currentYear);
  const previous = clients.filter(c => parseInt(c.latest_season_year) !== currentYear);

  const opt = c => `<option value="${c.id}" ${c.id === item.matched_client_id ? 'selected' : ''}>${c.org_name || c.name}</option>`;

  const clientOptions = [
    current.length  ? `<optgroup label="— ${currentYear} Directors —">${current.map(opt).join('')}</optgroup>` : '',
    previous.length ? `<optgroup label="— Previous Clients —">${previous.map(opt).join('')}</optgroup>` : '',
  ].join('');

  return `
    <div class="routing-panel">
      <div class="routing-label">Route to client:</div>
      <select id="route-client-select" class="routing-select">
        <option value="">— select client —</option>
        ${clientOptions}
      </select>
      <div class="routing-actions">
        <button class="btn-file" onclick="fileIntakeItem(${item.id})">✓ File & Mark Read in Gmail</button>
        <button class="btn-dismiss" onclick="dismissIntakeItem(${item.id})">Dismiss · Keep Unread</button>
        <button class="btn-dismiss" onclick="archiveIntakeItem(${item.id})">✕ Remove from Feed</button>
      </div>
    </div>
  `;
}

function renderAutoFiledStatus(item) {
  return `
    <div class="auto-filed-status">
      <span class="check-icon">✓</span>
      Auto-filed to ${item.client_name || 'client card'} · Marked read in Gmail
    </div>
    <div class="routing-actions" style="margin-top:0.75rem;">
      <button class="btn-dismiss" onclick="archiveIntakeItem(${item.id})">✕ Remove from Feed</button>
    </div>
  `;
}

function setupIntakeHandlers() {
  document.querySelectorAll('.source-tab').forEach(tab => {
    tab.addEventListener('click', () => {
      document.querySelectorAll('.source-tab').forEach(t => t.classList.remove('active'));
      tab.classList.add('active');
      state.intakeSource = tab.dataset.source;
      loadIntakeData();
    });
  });
}

// ── Global handlers (called from onclick attributes) ───────────────────────

window.selectIntakeItem = async function(id) {
  document.querySelectorAll('.feed-item').forEach(el => el.classList.remove('selected'));
  const el = document.querySelector(`.feed-item[data-id="${id}"]`);
  if (el) el.classList.add('selected');

  try {
    const items = await apiFetch('/api/intake/feed');
    const item = items.find(i => i.id === id);
    if (item) renderIntakeDetail(item);
  } catch (err) {
    console.error('Error loading intake item:', err);
  }
};

window.fileIntakeItem = async function(id) {
  const sel = document.getElementById('route-client-select');
  const clientId = sel && sel.value ? parseInt(sel.value) : null;
  try {
    await apiFetch(`/api/intake/${id}/action`, {
      method: 'POST',
      body: JSON.stringify({ action: 'file', client_id: clientId })
    });
    await loadIntakeData();
    const detail = document.getElementById('intake-detail');
    if (detail) detail.innerHTML = '<div class="detail-placeholder">Item filed ✓</div>';
  } catch (err) {
    console.error('Error filing item:', err);
    alert('Failed to file item');
  }
};

window.dismissIntakeItem = async function(id) {
  try {
    await apiFetch(`/api/intake/${id}/action`, {
      method: 'POST',
      body: JSON.stringify({ action: 'dismiss' })
    });
    await loadIntakeData();
    const detail = document.getElementById('intake-detail');
    if (detail) detail.innerHTML = '<div class="detail-placeholder">Item dismissed — stays unread in Gmail</div>';
  } catch (err) {
    console.error('Error dismissing item:', err);
  }
};

window.scanIntakeNow = async function() {
  const btn = document.getElementById('btn-scan-intake');
  if (btn) { btn.textContent = '⟳ Scanning…'; btn.disabled = true; }
  try {
    const result = await apiFetch('/api/intake/scan', { method: 'POST' });
    await loadIntakeData();
    if (btn) {
      const label = result.status === 'error'
        ? `⚠ ${result.error}`
        : `✓ ${result.classified ?? 0} new · ${result.scanned ?? 0} scanned`;
      btn.textContent = label;
      setTimeout(() => { btn.textContent = '⟳ Scan Now'; btn.disabled = false; }, 4000);
    }
  } catch (err) {
    console.error('Scan error:', err);
    if (btn) {
      btn.textContent = `⚠ Scan failed`;
      setTimeout(() => { btn.textContent = '⟳ Scan Now'; btn.disabled = false; }, 4000);
    }
  }
};

window.archiveIntakeItem = async function(id) {
  try {
    await apiFetch(`/api/intake/${id}/action`, {
      method: 'POST',
      body: JSON.stringify({ action: 'archive' })
    });
    await loadIntakeData();
    const detail = document.getElementById('intake-detail');
    if (detail) detail.innerHTML = '<div class="detail-placeholder">Removed from feed</div>';
  } catch (err) {
    console.error('Error archiving item:', err);
    alert('Failed to remove item');
  }
};
